import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Card";
import { H2 } from "components/text/headings";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  useToast,
} from "@chakra-ui/react";
import { FormSelect } from "../../../components/Forms";
import {
  CSRD_QUESTIONNAIRE_TYPES,
  generateArrayOfYears,
  showErrorToast,
  sortAlphabeticallyByAttribute,
} from "../../../utils";
import { useForm } from "react-hook-form";
import {
  useExportCSRDReport,
  useGetCSRDFiles,
  useGetCSRDQuestionsByType,
  useGetCSRDReport,
} from "../../../hooks/query/csrd";
import * as React from "react";
import { useState } from "react";
import { CSRDReportVirtualizedList } from "./components";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import AttachedFilesList from "../CSRDQuestionnaire/AttachedFilesList";

function CSRDReport() {
  const { t } = useTranslation("translation", {
    keyPrefix: "csrd.report",
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();

  const getFormDefaultValues = () => {
    const currentDate = new Date();
    return {
      year: (currentDate.getFullYear() - 1).toString(),
      type: CSRD_QUESTIONNAIRE_TYPES.ENVIRONMENTAL,
    };
  };

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
  } = useForm({
    defaultValues: getFormDefaultValues(),
  });

  const yearValue = watch("year");
  const typeValue = watch("type");

  const { data: questionsData } = useGetCSRDQuestionsByType(
    { type: typeValue },
    {
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: exportCSRDReport } = useExportCSRDReport({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const [mappedReportsData, setMappedReportsData] = useState([]);

  const mapReportsData = (data) => {
    const mappedData = [];

    if (data && typeof data !== "undefined" && Object.keys(data).length > 0) {
      Object.keys(data).forEach((topic) => {
        mappedData.push({
          topic: topic,
          questions:
            data[topic] && typeof data[topic] !== "undefined" && Object.keys(data[topic]).length > 0
              ? Object.keys(data[topic]).map((questionId) => {
                  const foundQuestionObj = questionsData.find((el) => el.id === questionId);
                  return {
                    rowIndex: foundQuestionObj?.rowIndex ?? -1,
                    question: foundQuestionObj?.question ?? "",
                    answers: data[topic][questionId].map((answerObj) => {
                      return {
                        userName: answerObj.userName ?? "",
                        answer: answerObj.answer ?? "",
                        dataSource: answerObj.dataSource ?? "",
                        comment: answerObj.comment ?? "",
                      };
                    }),
                  };
                })
              : [],
        });
      });
    }

    return mappedData;
  };

  const { data: answersData } = useGetCSRDReport(
    {
      year: yearValue,
      type: typeValue,
    },
    {
      onSuccess: (data) => {
        setMappedReportsData(mapReportsData(data));
      },
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled:
        questionsData !== null &&
        typeof questionsData !== "undefined" &&
        questionsData?.[0]?.type?.toLowerCase() === typeValue.toLowerCase(),
    }
  );

  const handleExport = (data) => {
    exportCSRDReport({
      params: {
        year: data.year,
        type: data.type?.toLowerCase(),
      },
    });
  };

  const { data: attachments } = useGetCSRDFiles(
    { type: typeValue ?? "", year: yearValue ?? "" },
    {
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: true,
    }
  );

  return (
    <Page title={t("page.title")}>
      <Card>
        <chakra.form id="csrdReportForm" marginBottom={4} onSubmit={handleSubmit(handleExport)}>
          <Flex gap="4" marginBlockStart="5" alignItems="end" justifyContent="space-between">
            <Flex width="50%" gap="4">
              <FormSelect label={t("inputs.financialYear")} errors={errors} {...register("year", { required: true })}>
                {generateArrayOfYears().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </FormSelect>
              <FormSelect label={t("inputs.type")} errors={errors} {...register("type", { required: true })}>
                {Object.keys(CSRD_QUESTIONNAIRE_TYPES).map((el) => {
                  return (
                    <option key={el} value={el}>
                      {globalTranslation(`csrd.questionnaireTypes.${el}`)}
                    </option>
                  );
                })}
              </FormSelect>
            </Flex>
            <Button
              colorScheme="customScheme"
              width="32"
              type="submit"
              isDisabled={!answersData || typeof answersData === "undefined" || Object.keys(answersData)?.length === 0}
              title={
                !answersData || typeof answersData === "undefined" || Object.keys(answersData)?.length === 0
                  ? t("inputs.noDataAvailableForExport")
                  : t("inputs.export")
              }
            >
              {t("inputs.export")}
            </Button>
          </Flex>
          <AttachedFilesList attachments={attachments} />
        </chakra.form>
        {mappedReportsData.length > 0 ? (
          <Accordion allowToggle={true}>
            {mappedReportsData
              .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "topic"))
              .map((dataElement) => {
                return (
                  <AccordionItem key={dataElement.topic}>
                    <h2>
                      <AccordionButton>
                        <Box flex="1" textAlign="left" justifyContent="center">
                          <Heading size="md">Topic: {dataElement.topic}</Heading>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pt={2} pb={5} overflow="auto">
                      <CSRDReportVirtualizedList
                        items={dataElement.questions.sort((a, b) => a.rowIndex - b.rowIndex)}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
          </Accordion>
        ) : (
          <H2>{globalTranslation("noDataAvailable")}</H2>
        )}
      </Card>
    </Page>
  );
}

export default WithAuthorization(CSRDReport);
